// ─── OJT Hub — AI Match Engine ────────────────────────────────────────────────
// Computes student ↔ job fit using two layers:
//   1. Instant keyword heuristic (synchronous, shown immediately)
//   2. Claude deep semantic score (async, cached in match_scores table)

import { supabase } from './supabase';

const ANTHROPIC_PROXY = "https://sghmpmjszcuvfdinsbxq.supabase.co/functions/v1/anthropic-proxy";

// ── In-memory cache so we don't re-fetch within a session ─────────────────────
const _cache = new Map(); // key: `${studentId}:${jobId}`

// ── Supabase client (reuse window singleton) ──────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// LAYER 1: Instant keyword score (0–100)
// ─────────────────────────────────────────────────────────────────────────────
const COURSE_MAP = {
  "computer science":       ["software","developer","engineer","tech","it","data","web","backend","frontend","fullstack","devops","cloud","programming","coding"],
  "information technology": ["it","tech","support","network","software","web","developer","systems","helpdesk","infrastructure"],
  "information systems":    ["systems","analyst","business","tech","it","erp","data","process","digital"],
  "computer engineering":   ["hardware","embedded","software","engineer","iot","systems","firmware","electronics"],
  "business administration":["business","admin","management","operations","hr","finance","marketing","sales","executive","coordinator"],
  "marketing":              ["marketing","brand","content","social media","digital","seo","ads","campaign","communications","creative"],
  "accounting":             ["accounting","finance","audit","tax","bookkeeping","treasury","financial","reporting","payroll"],
  "engineering":            ["engineering","technical","operations","design","manufacturing","quality","production","maintenance"],
  "communication":          ["communication","media","pr","content","writing","journalism","advertising","broadcast","publishing"],
  "nursing":                ["healthcare","medical","hospital","nursing","clinical","patient","health","pharmacy"],
  "education":              ["teaching","education","training","curriculum","academic","tutor","instruction"],
  "psychology":             ["hr","people","counseling","research","behavioral","social","mental health","welfare"],
  "architecture":           ["architecture","design","interior","construction","planning","drafting","urban"],
  "tourism":                ["tourism","hospitality","hotel","travel","events","front desk","concierge","food"],
  "journalism":             ["writing","media","content","editorial","reporter","news","publishing","broadcast"],
  "graphic design":         ["design","creative","visual","graphic","ui","ux","illustrat","branding","art"],
  "data science":           ["data","analytics","machine learning","ai","statistics","python","r","visualization","research"],
  "entrepreneurship":       ["startup","business","operations","product","strategy","ventures","innovation"],
};

function normalize(s) { return (s || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").trim(); }
function tokens(s)    { return normalize(s).split(/[\s,;|]+/).filter(w => w.length > 2); }

export function computeKeywordScore(job, profile) {
  if (!profile) return null;
  if (!profile.skills && !profile.course && !profile.summary) return null;

  const studentSkills = tokens(profile.skills || "");
  const jobTokens = [
    ...tokens(job.tags?.join(" ") || ""),
    ...tokens(job.title || ""),
    ...tokens((job.desc || job.description || "").slice(0, 600)),
  ];

  // Skills match (50 pts)
  let skillHits = 0;
  const matchedSkills = [];
  const uniqueStudentSkills = [...new Set(studentSkills)];
  uniqueStudentSkills.forEach(sk => {
    if (jobTokens.some(jt => jt.includes(sk) || sk.includes(jt))) {
      skillHits++;
      matchedSkills.push(sk);
    }
  });
  const skillScore = uniqueStudentSkills.length > 0
    ? Math.min((skillHits / Math.min(uniqueStudentSkills.length, 8)) * 50, 50)
    : 0;

  // Course/title match (30 pts)
  const courseLower = normalize(profile.course || "");
  const titleLower  = normalize(job.title || "");
  const descLower   = normalize((job.desc || job.description || "").slice(0, 400));
  let courseScore = 0;
  const courseWords = tokens(profile.course || "");
  const titleWords  = tokens(job.title || "");
  const directOverlap = courseWords.filter(w => w.length > 3 && titleWords.some(t => t.includes(w) || w.includes(t)));
  if (directOverlap.length > 0) {
    courseScore = 30;
  } else {
    for (const [key, keywords] of Object.entries(COURSE_MAP)) {
      if (courseLower.includes(key) || key.split(" ").some(k => k.length > 3 && courseLower.includes(k))) {
        const hits = keywords.filter(kw => titleLower.includes(kw) || descLower.includes(kw));
        if (hits.length >= 2) { courseScore = 30; break; }
        if (hits.length === 1) courseScore = Math.max(courseScore, 15);
      }
    }
  }

  // Location match (20 pts)
  const studentLoc = normalize(profile.location || "");
  const jobLoc     = normalize(job.location || "");
  const PH_CITIES  = ["manila","makati","bgc","taguig","quezon city","pasig","mandaluyong","ortigas","cebu","davao","alabang","muntinlupa","paranaque","antipolo","marikina","las pinas","valenzuela","caloocan"];
  let locScore = 10; // default PH bonus
  const studentCity = PH_CITIES.find(c => studentLoc.includes(c));
  const jobCity     = PH_CITIES.find(c => jobLoc.includes(c));
  if (studentCity && jobCity) {
    locScore = studentCity === jobCity ? 20 : 12;
  } else if (studentLoc && jobLoc && (jobLoc.includes(studentLoc.split(" ")[0]) || studentLoc.includes(jobLoc.split(" ")[0]))) {
    locScore = 15;
  }

  const total = Math.round(skillScore + courseScore + locScore);
  return {
    score: Math.min(Math.max(total, 5), 100),
    matchedSkills,
    breakdown: {
      skills:   Math.round(skillScore),
      course:   courseScore,
      location: locScore,
    },
    isAI: false,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 2: Claude deep semantic score
// Prompt engineered for Philippine OJT context
// ─────────────────────────────────────────────────────────────────────────────
async function computeAIScore(job, profile) {
  const prompt = `You are an OJT placement expert for Philippine universities. Score this student's fit for this OJT position.

STUDENT PROFILE:
- Name: ${profile.full_name || "Student"}
- Course: ${profile.course || "N/A"}
- Year Level: ${profile.year_level || "N/A"}
- GPA: ${profile.gpa || "N/A"}
- Skills: ${profile.skills || "None listed"}
- Summary: ${profile.summary || "None"}
- Experience: ${profile.experience || "None"}
- Certifications: ${profile.certifications || "None"}
- Location: ${profile.location || "Philippines"}

JOB POSITION:
- Title: ${job.title}
- Company: ${job.company || job.company_name || "Company"}
- Location: ${job.location || "Philippines"}
- Tags/Skills Required: ${(job.tags || []).join(", ") || "N/A"}
- Description: ${(job.desc || job.description || "").slice(0, 500) || "N/A"}

Respond ONLY with a JSON object, no markdown, no explanation outside the JSON:
{
  "score": <integer 0-100>,
  "reasoning": "<2 sentences max explaining the score>",
  "matched_skills": ["skill1", "skill2"],
  "skill_gaps": ["gap1", "gap2"],
  "strengths": ["strength1", "strength2"],
  "recommendation": "<one of: strong_match | good_match | fair_match | low_match>"
}`;

  const { data: { session: _s1 } } = await supabase.auth.getSession();
  const res = await fetch("https://sghmpmjszcuvfdinsbxq.supabase.co/functions/v1/anthropic-proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${_s1?.access_token ?? ""}` },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 400,
      system: "You are a precise OJT placement scoring system. Always respond with valid JSON only.",
      messages: [{ role: "user", content: prompt }],
    }),
  });

  if (!res.ok) throw new Error("Claude API error: " + res.status);
  const data = await res.json();
  const text = data.content?.[0]?.text || "";
  const clean = text.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN: getMatchScore
// Returns keyword score immediately via callback, then upgrades to AI score
// Caches results in Supabase match_scores table
// ─────────────────────────────────────────────────────────────────────────────
export async function getMatchScore({
  job,
  profile,
  studentId,
  onKeywordScore,   // callback(keywordResult) — called instantly
  onAIScore,        // callback(aiResult) — called when Claude responds
  forceRefresh = false,
}) {
  if (!profile || !job) return null;
  const cacheKey = `${studentId}:${job.id}`;

  // 1. Compute & emit keyword score immediately
  const kw = computeKeywordScore(job, profile);
  if (kw) onKeywordScore?.(kw);

  // 2. Check in-memory cache
  if (!forceRefresh && _cache.has(cacheKey)) {
    const cached = _cache.get(cacheKey);
    onAIScore?.(cached);
    return cached;
  }

  // 3. Check Supabase cache (score < 24h old)
  if (!forceRefresh && studentId) {
    try {
      const sb = supabase;
      const { data: cached } = await sb
        .from("match_scores")
        .select("*")
        .eq("student_id", studentId)
        .eq("job_id", job.id)
        .single();

      if (cached && cached.ai_score !== null) {
        const ageHours = (Date.now() - new Date(cached.computed_at)) / 3600000;
        if (ageHours < 24) {
          const result = {
            score:         cached.score,
            matchedSkills: cached.matched_skills || [],
            skillGaps:     cached.skill_gaps || [],
            reasoning:     cached.reasoning || "",
            strengths:     cached.strengths || [],
            breakdown:     kw?.breakdown || { skills: 0, course: 0, location: 0 },
            isAI:          true,
            recommendation: scoreToRecommendation(cached.score),
          };
          _cache.set(cacheKey, result);
          onAIScore?.(result);
          return result;
        }
      }
    } catch (_) { /* table may not exist yet, fall through */ }
  }

  // 4. Call Claude
  // proxy always available
  try {
    const ai = await computeAIScore(job, profile);
    const result = {
      score:         ai.score,
      matchedSkills: ai.matched_skills || kw?.matchedSkills || [],
      skillGaps:     ai.skill_gaps || [],
      reasoning:     ai.reasoning || "",
      strengths:     ai.strengths || [],
      breakdown:     kw?.breakdown || { skills: 0, course: 0, location: 0 },
      isAI:          true,
      recommendation: ai.recommendation || scoreToRecommendation(ai.score),
    };

    // 5. Cache in memory
    _cache.set(cacheKey, result);

    // 6. Persist to Supabase (fire and forget)
    if (studentId) {
      try {
        const sb = supabase;
        await supabase.from("match_scores").upsert({
          student_id:    studentId,
          job_id:        job.id,
          score:         result.score,
          keyword_score: kw?.score,
          ai_score:      result.score,
          matched_skills: result.matchedSkills,
          skill_gaps:    result.skillGaps,
          reasoning:     result.reasoning,
          strengths:     result.strengths,
          computed_at:   new Date().toISOString(),
        }, { onConflict: "student_id,job_id" });
      } catch (_) { /* non-critical */ }
    }

    onAIScore?.(result);
    return result;
  } catch (err) {
    console.warn("AI match failed, using keyword score:", err.message);
    return kw;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk score: compute AI scores for a list of jobs (for employer ranking)
// Returns sorted array [{job, match}] highest score first
// ─────────────────────────────────────────────────────────────────────────────
export async function rankApplicants(applications, jobTitle, jobDescription, jobTags) {
  if (!applications?.length) return [];

  const jobSummary = {
    title: jobTitle,
    desc: jobDescription,
    tags: jobTags || [],
  };

  const prompt = `You are an OJT placement expert. Rank these ${applications.length} applicants for the role: "${jobTitle}".

${applications.map((a, i) => {
  const p = a.profiles || {};
  return `[${i + 1}] ${p.full_name || "Student"}
  Course: ${p.course || "N/A"} | Year: ${p.year_level || "N/A"} | GPA: ${p.gpa || "N/A"}
  Skills: ${p.skills || "None"} | Summary: ${(p.summary || "").slice(0, 100)}`;
}).join("\n\n")}

Job description: ${(jobDescription || "").slice(0, 400)}
Required skills/tags: ${(jobTags || []).join(", ")}

Respond ONLY with a JSON array, no markdown:
[{"index": 1, "score": 85, "tag": "Strong Match", "reason": "one sentence"}, ...]
One object per applicant, in original order (index = original position 1-N).`;

  try {
    const { data: { session: _s2 } } = await supabase.auth.getSession();
    const res = await fetch("https://sghmpmjszcuvfdinsbxq.supabase.co/functions/v1/anthropic-proxy", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${_s2?.access_token ?? ""}` },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: "You are a precise OJT ranking system. Always respond with valid JSON array only.",
        messages: [{ role: "user", content: prompt }],
      }),
    });
    const data = await res.json();
    const text = (data.content?.[0]?.text || "").replace(/```json|```/g, "").trim();
    const rankings = JSON.parse(text);
    return applications.map((app, i) => {
      const r = rankings.find(x => x.index === i + 1) || { score: 50, tag: "Match", reason: "" };
      return { ...app, aiRank: r };
    }).sort((a, b) => (b.aiRank?.score || 0) - (a.aiRank?.score || 0));
  } catch {
    return applications;
  }
}

function scoreToRecommendation(score) {
  if (score >= 80) return "strong_match";
  if (score >= 60) return "good_match";
  if (score >= 40) return "fair_match";
  return "low_match";
}

export { scoreToRecommendation };
